import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import { WISHLIST_CATEGORY_CONSTANTS, WISHLIST_ITEM_CONSTANTS } from "../_MODULES/_API";
import { WishlistCategoryModal } from "../_UI/WishlistCategoryModal";
import { WishlistItemModal } from "../_UI/WishlistItemModal";
import { WishlistCategoryWorkspaceModal } from "../_UI/WishlistCategoryWorkspaceModal";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { EmptyState } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { CredentialExportPickerModal } from "../../../../_UI/CredentialExportPickerModal";
import { downloadCredentialPdf, downloadCredentialTxt } from "../../../../_UI/CredentialDownload";
import { wishlistItemExportRows } from "../_UTILS/wishlistShare.util";
import "../_UI/_Styles/Wishlist.css";

/*
    La URL histórica /wishlist/:categoryId sigue siendo valida, pero ya no
    mantiene una segunda UI de cards distinta a Wish List. Abre exactamente
    el mismo workspace fullscreen que se usa al tocar una categoría desde
    /wishlist. Asi no hay dos experiencias que se desincronicen.
*/
export const WishlistCategoryViewMain = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();

    const [category, setCategory] = useState(null);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [focusItemId, setFocusItemId] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [exportPicker, setExportPicker] = useState(null);

    const categoryQuery = useApiQuery({ minTimeQuery: 180, enablePagination: false, startLoading: true });
    const categoriesQuery = useApiQuery({ minTimeQuery: 180, enablePagination: false });
    const itemsQuery = useApiQuery({ minTimeQuery: 180, enablePagination: false, startLoading: true });

    useEffect(() => {
        categoryQuery.apiGet({
            endpoint: WISHLIST_CATEGORY_CONSTANTS.DETAIL,
            isDetail: true,
            config: { pathParams: { id: categoryId } },
        });
        categoriesQuery.apiGet({ endpoint: WISHLIST_CATEGORY_CONSTANTS.LIST });
        itemsQuery.apiGet({ endpoint: WISHLIST_ITEM_CONSTANTS.LIST, config: { params: { categoryId } } });
    }, [categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setCategory(categoryQuery.useApiData ?? null); }, [categoryQuery.useApiData]);
    useEffect(() => { setCategories(categoriesQuery.useApiData ?? []); }, [categoriesQuery.useApiData]);
    useEffect(() => { setItems(itemsQuery.useApiData ?? []); }, [itemsQuery.useApiData]);

    const openCreate = () => {
        setEditingItem(null);
        setItemModalOpen(true);
    };

    const openEdit = (item) => {
        setEditingItem(item);
        setItemModalOpen(true);
    };

    const patchLocal = (id, patch) => {
        setItems((rows) => rows.map((row) => String(row.id) === String(id) ? { ...row, ...patch } : row));
    };

    const toggleFavorite = async (item) => {
        const previous = !!item.isFavorite;
        const next = !previous;
        patchLocal(item.id, { isFavorite: next });
        const response = await onSilentPatch({
            endpoint: WISHLIST_ITEM_CONSTANTS.UPDATE,
            id: item.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del artículo.",
        });
        if (!response.success) patchLocal(item.id, { isFavorite: previous });
    };

    const togglePurchased = async (item) => {
        const previous = !!item.isPurchased;
        const next = !previous;
        patchLocal(item.id, { isPurchased: next, purchasedAt: next ? new Date().toISOString() : null });
        const response = await onSilentPatch({
            endpoint: WISHLIST_ITEM_CONSTANTS.UPDATE,
            id: item.id,
            data: { isPurchased: next },
            errorMessage: "No se pudo guardar el estado de compra.",
        });
        if (!response.success) patchLocal(item.id, { isPurchased: previous, purchasedAt: item.purchasedAt ?? null });
    };

    const archiveItem = async (item) => {
        const ok = await confirm.danger(
            "Archivar artículo",
            `Se archivará "${item.name}". Lo podés restaurar desde Archivados.`,
            { confirmText: "Archivar" },
        );
        if (!ok) return;

        setItems((rows) => rows.filter((row) => String(row.id) !== String(item.id)));
        const response = await onApiAction({
            method: "PATCH",
            endpoint: WISHLIST_ITEM_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: item.id },
            successMessage: "Artículo archivado",
        });
        if (!response.success) setItems((rows) => [item, ...rows]);
    };

    const loading = categoryQuery.useApiLoading || itemsQuery.useApiLoading;

    if (!loading && !category) {
        return <ViewLayout>
            <EmptyState
                icon={<FaHeart />}
                title="No encontramos esa categoría"
                action={<AppButton to="/wishlist">Volver a Wish List</AppButton>}
            />
        </ViewLayout>;
    }

    return <ViewLayout>
        <WishlistCategoryWorkspaceModal
            isOpen={!!category}
            category={category}
            items={items}
            focusItemId={focusItemId}
            onFocusItemHandled={() => setFocusItemId(null)}
            onClose={() => navigate("/wishlist")}
            onNewItem={openCreate}
            onEditItem={openEdit}
            onEditCategory={() => setCategoryModalOpen(true)}
            onExportItem={(item, currentCategory) => setExportPicker({ item, rows: wishlistItemExportRows(item, currentCategory?.name || category?.name || "") })}
            onToggleFavorite={toggleFavorite}
            onTogglePurchased={togglePurchased}
            onArchiveItem={archiveItem}
        />


        <CredentialExportPickerModal
            isOpen={!!exportPicker}
            title={exportPicker?.item ? `Guardar · ${exportPicker.item.name}` : "Guardar artículo"}
            rows={exportPicker?.rows ?? []}
            onClose={() => setExportPicker(null)}
            onExport={(format, selectedRows) => {
                const item = exportPicker?.item;
                if (!item) return;
                const payload = { title: `Wish List · ${item.name}`, rows: selectedRows, fileName: `wishlist-${item.name}` };
                if (format === "pdf") downloadCredentialPdf(payload); else downloadCredentialTxt(payload);
                setExportPicker(null);
            }}
        />

        <WishlistItemModal
            isOpen={itemModalOpen}
            item={editingItem}
            categories={categories}
            defaultCategoryId={categoryId}
            isCategoryLocked={!editingItem}
            onClose={() => { setItemModalOpen(false); setEditingItem(null); }}
            onSaved={(saved, isEdit) => {
                if (!saved?.id) return;
                if (String(saved.categoryId || "") !== String(categoryId || "")) {
                    setItems((rows) => rows.filter((row) => String(row.id) !== String(saved.id)));
                    setItemModalOpen(false);
                    setEditingItem(null);
                    return;
                }
                setItems((rows) => isEdit
                    ? rows.map((row) => String(row.id) === String(saved.id) ? { ...row, ...saved } : row)
                    : [saved, ...rows]);
                setFocusItemId(saved.id);
                setItemModalOpen(false);
                setEditingItem(null);
            }}
        />

        <WishlistCategoryModal
            isOpen={categoryModalOpen}
            category={category}
            onClose={() => setCategoryModalOpen(false)}
            onSaved={(saved) => {
                if (!saved?.id) return;
                setCategory((current) => ({ ...(current || {}), ...saved }));
                setCategories((rows) => rows.map((row) => String(row.id) === String(saved.id) ? { ...row, ...saved } : row));
                setCategoryModalOpen(false);
            }}
        />
    </ViewLayout>;
};
